import { selector } from "recoil";
import {
  IUserSuggestionsForSearchCacheState,
  UserSuggestionsForSearchState,
} from "./userSuggestionsForSearch";

import { AllstarUser } from "./index";
import { ISearchItem } from "MVVM/Components/SearchBar";
import { randomAvatar } from "MVVM/Utilities/Images";

interface GraphQLUser {
  _id: string;
  username?: string;
  avatar?: string;
  userGroup?: {
    type: string;
  };
  rank?: number;
  reason?: string;
  steam?: {
    steamid?: string;
    personaname?: string;
  };
}

const toSearchItem = (user: GraphQLUser): ISearchItem => ({
  id: user._id,
  name: user.username!,
  image: user.avatar || randomAvatar(),
  userGroup: user.userGroup,
  rank: user.rank,
  reason: user.reason,
  steam: user.steam,
});

const filterAndMapUsers = (
  users: GraphQLUser[],
  maxCount: number = 20,
  showOnlySteamAccounts: boolean = false,
): ISearchItem[] => {
  return users
    .filter(
      (user) =>
        user._id &&
        user.username &&
        (!showOnlySteamAccounts || user.steam?.personaname),
    )
    .slice(0, maxCount)
    .map(toSearchItem);
};

const getAllUsers = (followersData: any): GraphQLUser[] => {
  let allUsers: GraphQLUser[] = [];

  if (Array.isArray(followersData)) {
    allUsers = followersData;
  } else if (followersData?.followers && followersData?.following) {
    allUsers = [...followersData.followers, ...followersData.following];
  } else if (followersData?.profiles) {
    allUsers = [
      ...(followersData.profiles.followers || []),
      ...(followersData.profiles.following || []),
    ];
  }

  const seenSourceIds = new Set<string>();
  return allUsers.filter((user) => {
    if (!user._id || seenSourceIds.has(user._id)) {
      return false;
    }
    seenSourceIds.add(user._id);
    return true;
  });
};

const getSuggestedUsers = (
  suggestedUsersCacheState: IUserSuggestionsForSearchCacheState,
  isLoggedIn: boolean,
): GraphQLUser[] => {
  const allSuggestedUsers: GraphQLUser[] = [];
  const loginStatePrefix = isLoggedIn ? "logged-in" : "logged-out";

  Object.entries(suggestedUsersCacheState).forEach(([cacheKey, cacheState]) => {
    const shouldInclude =
      cacheKey.startsWith(loginStatePrefix) && cacheState.suggestedFollowers;

    if (shouldInclude) {
      allSuggestedUsers.push(...cacheState.suggestedFollowers);
    }
  });

  const seenIds = new Set<string>();
  return allSuggestedUsers.filter((user) => {
    if (seenIds.has(user._id)) return false;
    seenIds.add(user._id);
    return true;
  });
};

export const MergedSuggestedUsersSelector = selector<ISearchItem[]>({
  key: "MergedSuggestedUsersSelector_v3_default",
  get: ({ get }) => {
    const suggestedUsersCacheState = get(UserSuggestionsForSearchState);
    const allstarUser = get(AllstarUser);
    const followersData = allstarUser?.followers;
    const isLoggedIn = !!allstarUser?.user?._id;

    const uniqueSuggestedUsers = getSuggestedUsers(
      suggestedUsersCacheState,
      isLoggedIn,
    );
    const suggestedUsers = filterAndMapUsers(uniqueSuggestedUsers, 10);
    const usersFollowing = filterAndMapUsers(
      getAllUsers(followersData),
      20 - suggestedUsers.length,
    );

    const combinedUsers = [...suggestedUsers, ...usersFollowing];

    const finalSeenIds = new Set<string>();
    const uniqueCombinedUsers = combinedUsers.filter((user) => {
      if (finalSeenIds.has(user.id)) return false;
      finalSeenIds.add(user.id);
      return true;
    });

    return [...uniqueCombinedUsers];
  },
});

export const MergedSuggestedUsersMatchHistorySelector = selector<ISearchItem[]>(
  {
    key: "MergedSuggestedUsersSelector_v3_matchHistory",
    get: ({ get }) => {
      const suggestedUsersCacheState = get(UserSuggestionsForSearchState);
      const allstarUser = get(AllstarUser);
      const isLoggedIn = !!allstarUser?.user?._id;

      const uniqueSuggestedUsers = getSuggestedUsers(
        suggestedUsersCacheState,
        isLoggedIn,
      );
      const suggestedUsers = filterAndMapUsers(uniqueSuggestedUsers, 10, true);

      return [...suggestedUsers];
    },
  },
);
